if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        // SliderComponent can emit slideChanged with an undefined currentElement (its
        // sliderItemsToShow[currentPage - 1] lookup goes out of range whenever the list is not
        // actually a scrolling slider — e.g. the stacked desktop gallery at wide viewports).
        // Unguarded this threw "Cannot read properties of undefined (reading 'dataset')" and
        // killed every listener after it on the page. Surfaced once mobile_thumbnails was
        // enabled, because the constructor only registers this listener when thumbnails exist.
        const currentElement = event?.detail?.currentElement;
        if (!currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        // `prepend` is only ever true from the variant path (product-info.js calls
        // setActiveMedia(id, true) on colour change); a thumbnail click passes false. So it
        // doubles as "this update was NOT initiated by the shopper clicking a picture", and
        // that distinction is what the two guards below need.
        //
        // Client, 2026-08-28: "changing colour feels jumpy — no sudden movement". Measured at
        // 1440x900 with the page scrolled to y=900, one swatch click moved the window to y=85:
        // an 815px lurch, plus the whole media column visibly reshuffling. Both effects are
        // Dawn defaults that only make sense for the layouts they were written for, and neither
        // survives contact with this theme:
        //
        //   - The reorder assumes a one-at-a-time viewer where the active image must be moved
        //     into view. This gallery is `gallery_layout: stacked` — every image is already on
        //     screen — so prepending buys nothing and costs a full reshuffle. Worse, product-
        //     info.js re-sorts to the server's canonical order first, so from the second click
        //     on the shopper sees two reorders per click. Leaving the order alone also keeps
        //     pn-gallery.js's dot indices, which are built against the original NodeList order,
        //     in sync with what is on screen.
        //   - The window scroll assumes the shopper asked to look at a specific picture. On a
        //     variant change they asked to change colour, and yanking the page is the "sudden
        //     movement" being reported.
        //
        // A thumbnail click keeps the window scroll untouched: there the shopper did ask to be
        // taken to that image. The reorder is gone outright rather than guarded — `prepend` is
        // true on exactly one call path, so a guarded version would have been dead code. The
        // slider still gets its page count refreshed, because the active item changed.
        const variantDriven = prepend === true;

        if (this.elements.viewer.slider) this.elements.viewer.resetPages();

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Horizontal, inside the track only — on mobile this is what walks the slider to the
          // newly selected colour, and it replaces what the reorder used to do. Never scrolls
          // the page.
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (variantDriven) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        // Same class of crash: a media id with no matching thumbnail (a variant-filtered image)
        // left activeThumbnail null and the announce call threw.
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
